import type { Dispatch, SetStateAction } from "react";
import { useCallback } from "react";

import type { ModelExpertModifiedConfigResponse } from "@citrine/resources/catalyst";

import type { CatalystPredictorState } from "#src/contexts/catalyst";

import type {
  CatalystQuestionAndAnswer,
  ModifiedConfigModelQuestionAndAnswer,
  QuestionAndAnswerState,
} from "../types";
import { isModifiedConfigModelQuestionAndAnswer } from "../types";

/** Callbacks used for the Apply/Cancel buttons associated with Model Expert
 *  modified-config answers.
 **/
export function useApplyAndRejectModelExpertChanges({
  predictor,
  setQuestionsAndAnswers,
}: {
  predictor: CatalystPredictorState | undefined;
  setQuestionsAndAnswers: Dispatch<SetStateAction<CatalystQuestionAndAnswer[]>>;
}) {
  const applyPredictorChange = useCallback(
    async (
      questionAndAnswerId: string,
      modifiedConfig: ModelExpertModifiedConfigResponse,
    ) => {
      // Shouldn't happen since we resolve unacted-upon model config changes
      // with the mode/predictor observers.
      /* v8 ignore if */
      if (!predictor) {
        return;
      }

      setQuestionsAndAnswers((prevQuestionsAndAnswers) =>
        prevQuestionsAndAnswers.map((questionAndAnswer) =>
          questionAndAnswer.id === questionAndAnswerId &&
          isModifiedConfigModelQuestionAndAnswer(questionAndAnswer)
            ? ({
                ...questionAndAnswer,
                state: "applying",
              } satisfies ModifiedConfigModelQuestionAndAnswer)
            : questionAndAnswer,
        ),
      );

      // If the request to update the predictor fails, just put it back in an
      // idle state for the time being (a snackbar notification will be displayed).
      // Idk how often this happens, but https://citrine.atlassian.net/browse/PNE-481
      // is one scenario.
      let state: QuestionAndAnswerState = "idle";

      try {
        await predictor.updatePredictor(modifiedConfig);
        state = "applied";
      } finally {
        setQuestionsAndAnswers((prevQuestionsAndAnswers) =>
          prevQuestionsAndAnswers.map((questionAndAnswer) =>
            questionAndAnswer.id === questionAndAnswerId &&
            isModifiedConfigModelQuestionAndAnswer(questionAndAnswer)
              ? ({
                  ...questionAndAnswer,
                  state,
                } satisfies ModifiedConfigModelQuestionAndAnswer)
              : questionAndAnswer,
          ),
        );
      }
    },
    [predictor, setQuestionsAndAnswers],
  );

  const rejectPredictorChange = useCallback(
    (questionAndAnswerId: string) => {
      setQuestionsAndAnswers((prevQuestionsAndAnswers) =>
        prevQuestionsAndAnswers.map((questionAndAnswer) =>
          questionAndAnswer.id === questionAndAnswerId &&
          isModifiedConfigModelQuestionAndAnswer(questionAndAnswer)
            ? ({
                ...questionAndAnswer,
                state: "rejected",
              } satisfies ModifiedConfigModelQuestionAndAnswer)
            : questionAndAnswer,
        ),
      );
    },
    [setQuestionsAndAnswers],
  );

  return {
    applyPredictorChange,
    rejectPredictorChange,
  };
}
